import {
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridSingleSelectColDef,
} from '@mui/x-data-grid'
import { FilterCapabilityConfig } from '@omnea-app/packages/backend/filtering/types/baseFilterTypes'
import {
  ColumnCapabilities,
  createColumnCapabilities,
} from '@omnea-app/packages/backend/filtering/utils/createColumnCapabilities'
import { getDateFilterOperators } from '../../columns/definitions/getDateFilterOperators'

const DEFAULT_COLUMN_CAPABILITIES: Partial<ColumnCapabilities> = {
  groupable: false,
  aggregable: false,
  availableAggregationFunctions: [],
}

/**
 * Maps backend scalar types to supported MUI DataGrid filter operators
 */
export const getFilterOperatorsForScalarType = (scalarType: string) => {
  const allStringOperators = getGridStringOperators()
  const allNumericOperators = getGridNumericOperators()
  const allDateOperators = getDateFilterOperators()
  const allSingleSelectOperators = getGridSingleSelectOperators()

  switch (scalarType) {
    case 'string':
      return allStringOperators.filter((op) =>
        ['equals', 'doesNotEqual', 'contains', 'doesNotContain', 'startsWith', 'isEmpty', 'isNotEmpty'].includes(op.value)
      )
    case 'number':
      return allNumericOperators.filter((op) => ['=', '!=', '>', '<', '>=', '<=', 'isEmpty', 'isNotEmpty'].includes(op.value))
    case 'boolean':
    case 'enum':
      // Use singleSelect operators for enum and boolean fields (shows dropdown UI)
      return allSingleSelectOperators.filter((op) =>
        ['equals', 'doesNotEqual', 'isEmpty', 'isNotEmpty', 'isAnyOf'].includes(op.value)
      )
    case 'date':
    case 'timestamp':
      return allDateOperators.filter((op) =>
        ['equals', 'doesNotEqual', 'before', 'after', 'onOrBefore', 'onOrAfter', 'isEmpty', 'isNotEmpty'].includes(op.value)
      )
    case 'uuid':
      return allStringOperators.filter((op) => ['equals', 'doesNotEqual'].includes(op.value))
    case 'json':
      return allStringOperators.filter((op) => ['equals', 'doesNotEqual', 'isEmpty', 'isNotEmpty'].includes(op.value))
    default:
      return allStringOperators
  }
}

/**
 * Generic function to apply backend column capabilities to MUI DataGrid column definitions
 * This ensures frontend column properties match backend support
 *
 * @param columns - MUI DataGrid column definitions
 * @param capabilities - Backend column capabilities mapping
 * @returns Updated column definitions with capabilities applied
 */
export function applyBackendColumnCapabilities<T extends string>(
  columns: GridColDef[],
  config: FilterCapabilityConfig
): GridColDef[] {
  const columnCapabilities = createColumnCapabilities({
    filterConfig: config,
  })

  return columns.map((column) => {
    const capability = columnCapabilities[column.field as T]

    if (!capability) {
      const isCustomField = typeof column.field === 'string' && column.field.startsWith('customField|')

      if (isCustomField) {
        // Custom fields are filterable but not sortable (requires complex database joins)
        return {
          ...column,
          sortable: false,
          filterable: true,
          filterOperators: column.filterOperators || getFilterOperatorsForScalarType('string'),
          ...DEFAULT_COLUMN_CAPABILITIES,
        }
      }

      // If no capability config found for non-custom fields, then we can't filter/sort/group
      return {
        ...column,
        sortable: false,
        filterable: false,
        ...DEFAULT_COLUMN_CAPABILITIES,
      }
    }

    // Get the backend field configuration
    const fieldConfig = config.filterableFields[column.field]
    const baseColumn: GridColDef = {
      ...column,
      sortable: capability.sortable && column.sortable !== false,
      filterable: capability.filterable && column.filterable !== false,
      groupable: capability.groupable && column.groupable !== false,
      ...DEFAULT_COLUMN_CAPABILITIES,
    }

    // Apply filter operators and column type based on backend field type
    if (fieldConfig?.type === 'scalar' && fieldConfig.scalarType) {
      const filterOperators = getFilterOperatorsForScalarType(fieldConfig.scalarType)

      // Only apply backend operators if the column doesn't already have custom filter operators
      const filterOperatorsToApply = column.filterOperators ? undefined : filterOperators

      // Set MUI column type for enum fields (enables singleSelect filter UI)
      if (fieldConfig.scalarType === 'enum' && fieldConfig.enumMapping) {
        return {
          ...baseColumn,
          filterOperators: filterOperatorsToApply,
          type: 'singleSelect',
          valueOptions: Object.keys(fieldConfig.enumMapping).map((displayValue) => ({
            value: displayValue,
            label: displayValue,
          })),
        } as GridSingleSelectColDef
      }

      // Set MUI column type for boolean fields (display as True/False dropdown)
      if (fieldConfig.scalarType === 'boolean') {
        return {
          ...baseColumn,
          filterOperators: filterOperatorsToApply,
          type: 'singleSelect',
          valueOptions: [
            { value: 'True', label: 'True' },
            { value: 'False', label: 'False' },
          ],
        } as GridSingleSelectColDef
      }

      // Apply filter operators for non-enum/boolean fields
      if (filterOperatorsToApply) {
        return {
          ...baseColumn,
          filterOperators: filterOperatorsToApply,
        }
      }
    }

    // Handle computed and relation fields - apply default string operators if no custom operators
    if ((fieldConfig?.type === 'computed' || fieldConfig?.type === 'relation') && !column.filterOperators) {
      return {
        ...baseColumn,
        filterOperators: getFilterOperatorsForScalarType('string'),
      }
    }

    return baseColumn
  })
}
